import { useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router';
import {
	initOneSignal,
	consumePendingNotification,
	rebindExternalUserId,
	clearExternalUserId,
} from '@/utils';
import { useLogClientMutation } from '@/store/api';
import { Lobby } from '@/api';
import { EventType } from '@sportspredict/shared';

const getNotificationData = (data: any) =>
	data?.additionalData ||
	data?.notification?.additionalData ||
	data?.payload?.additionalData ||
	data;

const getRouteFromValue = (value: unknown): string | null => {
	if (typeof value !== 'string' || !value.trim()) return null;

	const trimmedValue = value.trim();
	if (trimmedValue.startsWith('/')) return trimmedValue;

	try {
		const url = new URL(trimmedValue);
		return `${url.pathname}${url.search}${url.hash}`;
	} catch {
		return null;
	}
};

const getProbabilityHomeRouteFromRoute = (route: string | null): string | null => {
	if (!route?.startsWith('/probability-events/')) return null;

	const eventId = route.split('?')[0].split('/')[2];
	return eventId ? `/probability-events/${eventId}/home` : null;
};

const getProbabilityRoute = (data: any, additionalData: any): string | null => {
	const explicitRoute = getRouteFromValue(
		additionalData?.route || additionalData?.url || data?.url || data?.launchUrl
	);
	const explicitProbabilityHomeRoute = getProbabilityHomeRouteFromRoute(explicitRoute);

	if (explicitProbabilityHomeRoute) {
		return explicitProbabilityHomeRoute;
	}

	const eventId = additionalData?.eventId;
	const isProbabilityEvent =
		additionalData?.isProbabilityEvent === true ||
		additionalData?.eventType === EventType.Probability;

	if (!isProbabilityEvent || !eventId) return null;

	return `/probability-events/${eventId}/home`;
};

export function useOneSignalInit(userId: string | undefined): void {
	const [logClient] = useLogClientMutation();
	const navigate = useNavigate();
	const userIdRef = useRef(userId);

	// Keep the ref in sync so the visibility handler always has the latest userId
	useEffect(() => {
		userIdRef.current = userId;
	}, [userId]);

	const navigateToNotification = useCallback(
		async (data: any) => {
			const additionalData = getNotificationData(data);
			const probabilityRoute = getProbabilityRoute(data, additionalData);

			if (probabilityRoute) {
				console.log('[PUSH_NAV]: Handling probability notification click', { probabilityRoute });
				navigate(probabilityRoute);
				return;
			}

			const { eventId, matchId, lobbyId } = additionalData || {};

			if (!eventId && !lobbyId) return;

			console.log('[PUSH_NAV]: Handling notification click', { eventId, matchId, lobbyId });

			try {
				let targetLobbyId = lobbyId;
				if (!targetLobbyId && eventId) {
					const lobbies = await Lobby.getLobbiesByEventId(eventId);
					if (lobbies?.length) {
						targetLobbyId = lobbies[0].id;
					}
				}

				if (eventId && targetLobbyId) {
					if (additionalData?.isH2H) {
						navigate(`/head-to-head/${eventId}/${targetLobbyId}`);
					} else {
						navigate(`/LobbyDetails?id=${targetLobbyId}&eventId=${eventId}&tab=markets`);
					}
				} else if (eventId) {
					navigate(`/EventDetails?id=${eventId}`);
				} else if (targetLobbyId) {
					navigate(`/LobbyDetails?id=${targetLobbyId}&tab=markets`);
				}
			} catch (error) {
				console.error('[PUSH_NAV]: Redirection failed', error);
			}
		},
		[navigate]
	);

	// Main initialization effect — runs when userId changes (login/logout)
	useEffect(() => {
		if (!userId) {
			// User logged out — clear external ID so pushes don't reach the wrong person
			clearExternalUserId();
			return;
		}

		initOneSignal({
			userId,
			logClient,
		});

		// Process any notification click that arrived before React was ready
		const pendingData = consumePendingNotification();
		if (pendingData) {
			navigateToNotification(pendingData);
		}

		const handlePushClick = (event: any) => {
			navigateToNotification(event.detail);
		};

		window.addEventListener('PUSH_NOTIFICATION_CLICK', handlePushClick);

		return () => {
			window.removeEventListener('PUSH_NOTIFICATION_CLICK', handlePushClick);
		};
	}, [userId, logClient, navigateToNotification]);

	// Re-bind external user ID on app resume / foreground
	// This is the critical fix: if iOS rotated the APNs token while the app was
	// backgrounded, the native OneSignal SDK may have created a new subscription
	// that is not yet mapped to any external_id. Re-calling setExternalUserId
	// on every resume ensures the mapping stays current.
	useEffect(() => {
		const handleVisibilityChange = () => {
			if (document.visibilityState === 'visible' && userIdRef.current) {
				rebindExternalUserId(userIdRef.current);
			}
		};

		document.addEventListener('visibilitychange', handleVisibilityChange);

		return () => {
			document.removeEventListener('visibilitychange', handleVisibilityChange);
		};
	}, []);
}
